import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { TranslocoService } from "@ngneat/transloco";
import { Login } from "src/app/model/form/login";
import { CustomValidator } from "src/shared/custom-validator";
import { Message, MessageService } from "primeng/api";
import { Router } from "@angular/router";
import { AuthService } from "../../../service/auth.service";
import { CognitoException } from "src/app/model/enum/cognito-exception";
import { Title } from "@angular/platform-browser";
import { LANGUAGE_LIST } from "src/app/model/mockup/language-mockup";
import { LanguageEnum } from "src/app/model/enum/language-enum";
import { AccountService } from "src/app/service/account.service";
import { Language } from "src/app/model/language";
import { GeolocationService } from "src/app/service/geolocation.service";
import { ActivatedRoute } from '@angular/router';
import { LoginResponse } from 'src/app/model/response/auth-response';
import { firstValueFrom, race, timer, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { enviroment } from 'src/environments/environment';
import { SessionPersistenceService } from 'src/app/service/session-persistence.service';
import { AccountDeletionService, PENDING_DELETION_ROUTE } from 'src/app/service/account-deletion.service';
import { AuthLayoutComponent } from '../auth-layout/auth-layout.component';
import { isSessionStoreUnavailable } from 'src/app/model/session-store-unavailable';

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.css"],
})
export class LoginComponent implements OnInit, OnDestroy {
  @ViewChild(AuthLayoutComponent) private layout?: AuthLayoutComponent;
  public form!: FormGroup;
  public changePasswordForm!: FormGroup;
  codeParameter: string = "";
  messages: Message[];
  languageOptions: Language[];
  DEFAULT_LANGUAGE: Language;
  lastLanguage: Language;
  language: Language;
  userInteractedLanguage = false;
  dashboardUrl: string;
  createAccountUrl: string;
  forgotPasswordUrl: string;
  activateAccountUrl: string;
  mfaAccountUrl: string;
  submitBtnLoading: boolean = false;
  checkingSession: boolean = false;
  username: string | null = null;
  isNewPassRequired: boolean = false;
  messagesPassword: Message[] = [];
  session: string = "";
  showPassword = false;
  showNewPassword = false;
  rememberEmail = false;
  private static readonly REMEMBERED_EMAIL_KEY = 'rememberedEmail';
  
  // Controle de subscriptions e estado do componente
  private subscriptions: Subscription = new Subscription();
  private isComponentActive: boolean = true;


  email: string | null = null;
  password: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private titleService: Title,
    private translocoService: TranslocoService,
    private accountService: AccountService,
    private geolocationService: GeolocationService,
    private formBuilderPass: FormBuilder,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private sessionPersistence: SessionPersistenceService,
    private accountDeletion: AccountDeletionService
  ) {
    this.messages = [];
    this.languageOptions = LANGUAGE_LIST as [];
  // Fallback padrão deve ser EN_US
    this.DEFAULT_LANGUAGE = this.languageOptions[0];
    this.language = this.DEFAULT_LANGUAGE;
    this.lastLanguage = this.DEFAULT_LANGUAGE;
    this.dashboardUrl = "/my-garage";
    this.createAccountUrl = "/create-account";
    this.forgotPasswordUrl = "/forgot-password";
    this.activateAccountUrl = "/activate-account";
    this.mfaAccountUrl = "/mfa-login";
  }

  async ngOnInit() {
    // Normaliza language do sessionStorage
    const storedLang = this.normalizeLanguage(sessionStorage.getItem('language'));
    sessionStorage.setItem('language', storedLang);
    this.language = this.getLanguage(storedLang);
    this.createForm(new Login(this.language));
    this.restoreRememberedEmail();
    this.createChangePasswordForm();
    this.changeLanguage(this.language.name);
    this.setTitle();
    this.refreshLanguageOptions();
    
    // Geolocation assíncrona - não bloqueia
    if (storedLang === LanguageEnum.EN_US) {
      this.getLocationAsync();
    }
    
    // Subscription para email em lowercase - adicionada ao controle
    const emailSub = this.form.controls["email"].valueChanges.subscribe((value) => {
      if (value) {
        this.form.controls["email"].setValue(value.toLowerCase(), {
          emitEvent: false,
        });
      }
    });
    this.subscriptions.add(emailSub);

    // Verificação de sessão existente otimizada
    await this.checkExistingSession();

    // Verificar código de login automático - subscription controlada
    const queryParamsSub = this.route.queryParams.subscribe(params => {
      const code = params['code'];
      if (code && this.isComponentActive) {
        const decoded = atob(code);
        const [email, password] = decoded.split(':');
        this.email = email;
        this.password = password;
        this.form.patchValue({ email: this.email, password: this.password });
        this.automaticSubmit();
      }

      const oauthError = params['oauth_error'];
      if (oauthError && this.isComponentActive) {
        this.showMessage('error', oauthError);
      }

      // Deslogado à força pelo interceptor: 401 SESSION_EXPIRED. O motivo vem do `reason`
      // do backend, então cada causa tem sua própria mensagem. `unknown` (backend antigo,
      // sem `reason`) usa o texto neutro, que cita as possibilidades sem afirmar nenhuma.
      // `expired` é aceito por compatibilidade com URLs geradas por builds anteriores.
      const SESSION_ERROR_MESSAGES: Record<string, string> = {
        device_limit: 'user.login.sessionExpiredDeviceLimit',
        inactivity: 'user.login.sessionExpiredInactivity',
        unknown: 'user.login.sessionExpired',
        expired: 'user.login.sessionExpired',
      };
      const messageKey = SESSION_ERROR_MESSAGES[params['session_error']];
      if (messageKey && this.isComponentActive) {
        this.translate(messageKey).then((value) => {
          if (this.isComponentActive) this.showMessage('error', value);
        });
      }

      // Desafio de MFA encerrado (tentativas esgotadas ou sessão morta). Vem por URL
      // porque o toast da tela de MFA não sobrevive à navegação até aqui.
      const MFA_ERROR_MESSAGES: Record<string, string> = {
        session_ended: 'user.confirmLoginMfa.sessionEnded',
      };
      const mfaMessageKey = MFA_ERROR_MESSAGES[params['mfa_error']];
      if (mfaMessageKey && this.isComponentActive) {
        this.translate(mfaMessageKey).then((value) => {
          if (this.isComponentActive) this.showMessage('error', value);
        });
      }

      // Falha do login social por indisponibilidade temporária (503 no callback do OAuth). Vem
      // por URL porque o auth-callback navega pra cá; o código OAuth é de uso único, então a
      // pessoa precisa refazer o login social.
      const LOGIN_ERROR_MESSAGES: Record<string, string> = {
        session_store_unavailable: 'user.login.serviceUnavailable',
      };
      const loginErrorKey = LOGIN_ERROR_MESSAGES[params['login_error']];
      if (loginErrorKey && this.isComponentActive) {
        this.translate(loginErrorKey).then((value) => {
          if (this.isComponentActive) this.showMessage('error', value);
        });
      }
    });
    this.subscriptions.add(queryParamsSub);
  }

  ngOnDestroy(): void {
    // Marcar componente como inativo para evitar navegações tardias
    this.isComponentActive = false;
    
    // Cancelar todas as subscriptions
    this.subscriptions.unsubscribe();
    
    //console.log('[LoginComponent] Componente destruído, subscriptions canceladas');
  }

  /**
   * Verifica sessão existente e navega se válida
   * Elimina duplicação de código e otimiza fluxo
   */
  private async checkExistingSession(): Promise<void> {
    const token = sessionStorage.getItem("token");
    const refreshToken = sessionStorage.getItem("refreshToken");

    if (!token) return;

    // Bloqueia o submit enquanto a sessão existente está sendo validada,
    // evitando que o usuário dispare um login manual durante o redirecionamento.
    this.checkingSession = true;

    try {
      // Tenta verificar token
      const isValid = await this.authService.verifyToken(token);
      
      // Verificar se componente ainda está ativo antes de navegar
      if (!this.isComponentActive) {
        //console.log('[LoginComponent] Componente inativo, cancelando navegação de checkExistingSession');
        return;
      }
      
      if (isValid) {
        this.authService.startTokenRefresh();
        await this.navigateToDashboardIfAuthorized();
        return;
      }

      // Se token inválido, tenta refresh
      if (refreshToken) {
        const refreshResult = await this.authService.refreshAuth();
        
        // Verificar novamente se componente ainda está ativo
        if (!this.isComponentActive) {
          //console.log('[LoginComponent] Componente inativo, cancelando navegação após refresh');
          return;
        }
        
        if (refreshResult === CognitoException.SUCCESS) {
          await this.navigateToDashboardIfAuthorized();
        }
      }
    } catch (error) {
      console.error('Erro ao verificar sessão:', error);
      // Falha silenciosa - usuário pode fazer login manualmente
    } finally {
      this.checkingSession = false;
    }
  }

  /**
   * Navega para dashboard se usuário autorizado
   * Centraliza lógica de navegação e usa cache
   */
  private async navigateToDashboardIfAuthorized(): Promise<void> {
    // Verificar se componente ainda está ativo antes de navegar
    if (!this.isComponentActive) {
      //console.log('[LoginComponent] Componente inativo, cancelando navigateToDashboardIfAuthorized');
      return;
    }
    
    try {
      // Busca dados do usuário (com cache do AuthService)
      const userData = await firstValueFrom(this.authService.getUserData());
      
      // Verificar novamente após operação assíncrona
      if (!this.isComponentActive) {
        //console.log('[LoginComponent] Componente inativo após getUserData, cancelando navegação');
        return;
      }
      
      if (!userData) {
        this.showMessage("error", await this.translate("user.login.errorLogin"));
        return;
      }

      if (userData.accessLevel < 1) {
        this.showMessage("error", await this.translate("user.login.accessDenied"));
        return;
      }

      // Navegar para dashboard
      this.sessionPersistence.persist();
      const redirect = this.authService.consumePostLoginRedirect();
      if (redirect) { this.router.navigateByUrl(redirect); return; }
      this.router.navigate([this.dashboardUrl]);
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      // 403 de conta em carência: o interceptor já levou à tela de desistência, não é falha.
      if (this.isComponentActive && !this.accountDeletion.isPending) {
        this.showMessage("error", await this.translate("errorConection"));
      }
    }
  }

  /**
   * Helper para tradução síncrona
   */
  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translocoService.selectTranslate(key));
  }

  /**
   * Geolocation assíncrona com fallback e timeout
   */
  private getLocationAsync(): void {
    // Timeout de 2 segundos para geolocation
    const timeout$ = timer(2000).pipe(map(() => null));
    const geolocation$ = this.geolocationService.getCountry();
    
    race([geolocation$, timeout$]).pipe(
      take(1)
    ).subscribe({
      next: (data: any) => {
        if (data && !this.userInteractedLanguage) {
          const geoLang = data.country_code === 'BR' 
            ? this.languageOptions[0] 
            : this.languageOptions[1];
          this.updateLanguage(geoLang);
        }
      },
      error: (_) => {
        // Falha silenciosa - já tem fallback
        //console.log('Geolocation não disponível, usando fallback');
      }
    });
  }

  /**
   * Atualiza idioma
   */
  private updateLanguage(lang: Language): void {
    this.form.get('language')?.setValue(lang);
    this.language = lang;
    this.lastLanguage = lang;
    sessionStorage.setItem('language', lang.name);
    this.changeLanguage(lang.name);
    this.refreshLanguageOptions();
  }

  loginWithGoogle(): void {
    const redirectUri = encodeURIComponent(`${enviroment.BASE_URL}/auth/callback`);
    // prompt=select_account: sem ele, o Managed Login do Cognito reaproveita o cookie de sessão
    // do domínio e entra direto com a última conta Google. O Cognito repassa o parâmetro ao
    // Google, que mostra o seletor de contas (o app já manda). Só no Google: a Apple não documenta prompt.
    window.location.href = `${enviroment.COGNITO_OAUTH_URL}/oauth2/authorize?identity_provider=Google&client_id=${enviroment.COGNITO_CLIENT_ID}&response_type=code&scope=${LoginComponent.SOCIAL_LOGIN_SCOPE}&redirect_uri=${redirectUri}&prompt=select_account`;
  }

  loginWithApple(): void {
    const redirectUri = encodeURIComponent(`${enviroment.BASE_URL}/auth/callback`);
    window.location.href = `${enviroment.COGNITO_OAUTH_URL}/oauth2/authorize?identity_provider=SignInWithApple&client_id=${enviroment.COGNITO_CLIENT_ID}&response_type=code&scope=${LoginComponent.SOCIAL_LOGIN_SCOPE}&redirect_uri=${redirectUri}`;
  }

  /**
   * Escopos do token do login social (Google e Apple). Sem aws.cognito.signin.user.admin o access
   * token só serve para openid/email/profile, e o Cognito recusa troca de senha, MFA e GetUser com
   * "Access Token does not have required scopes". O app client do Cognito precisa permitir esse
   * escopo em cada ambiente, senão o authorize devolve invalid_scope.
   */
  private static readonly SOCIAL_LOGIN_SCOPE = 'openid+email+profile+aws.cognito.signin.user.admin';

  goToWelcome() {
    this.router.navigate(['/forgot-password'], { queryParams: { lang: 'en-us' } });
  }

  createForm(login: Login) {
    this.form = this.formBuilder.group({
      email: [login.email, CustomValidator.getEmail()],
      password: [login.password, CustomValidator.getRequired()],
      language: [login.language ?? 'en-us'],
    });
  }

  signUp() {
    sessionStorage.setItem("language", this.translocoService.getActiveLang());
    this.router.navigate([this.createAccountUrl]);
  }

  forgotPassword() {
    this.router.navigate([this.forgotPasswordUrl]);
  }

  async onSubmit() {
    // O botão já fica desabilitado nesses estados, mas o Enter no formulário
    // dispara o ngSubmit direto — então a guarda também vive aqui.
    if (this.checkingSession || this.submitBtnLoading) return;

    if (!this.form.valid) {
      this.showMessage("error", await this.translate("user.login.errorLogin"));
      return;
    }

    this.submitBtnLoading = true;
    this.layout?.scene?.launch();

    try {
      sessionStorage.setItem("email", this.f.email.value);
      const result = await this.authService.initiateAuth(
        this.f.email.value, 
        this.f.password.value
      );

      this.persistRememberedEmail(result);
      await this.handleAuthResult(result);
    } catch (error) {
      await this.handleAuthError(error);
    } finally {
      this.submitBtnLoading = false;
    }
  }

  private restoreRememberedEmail(): void {
    const remembered = localStorage.getItem(LoginComponent.REMEMBERED_EMAIL_KEY);
    if (!remembered) return;
    this.form.patchValue({ email: remembered });
    this.rememberEmail = true;
  }

  private persistRememberedEmail(result: LoginResponse): void {
    const accepted = !!result.authenticationResult ||
      ['PASSWORD_VERIFIER', 'CUSTOM_CHALLENGE', 'NEW_PASSWORD_REQUIRED'].includes(result.challengeName ?? '');
    if (!accepted) return;

    if (this.rememberEmail) {
      localStorage.setItem(LoginComponent.REMEMBERED_EMAIL_KEY, String(this.f.email.value).toLowerCase());
    } else {
      localStorage.removeItem(LoginComponent.REMEMBERED_EMAIL_KEY);
    }
  }

  /**
   * Processa resultado da autenticação
   */
  private async handleAuthResult(result: LoginResponse): Promise<void> {
    // Verificar se componente ainda está ativo antes de processar
    if (!this.isComponentActive) {
      return;
    }
    
    // Login direto - tokens já armazenados pelo CognitoService
    if (result.authenticationResult) {
      // Conta em carência de exclusão: o /user/active abaixo daria 403. Vai direto para a tela
      // de desistência, sem consumir o postLoginRedirect (serve se a pessoa desistir).
      if (this.accountDeletion.isPending) {
        this.sessionPersistence.persist();
        this.router.navigate([PENDING_DELETION_ROUTE]);
        return;
      }

      const token = sessionStorage.getItem("token");
      if (token) {
        // Usa cache do AuthService
        const userData = await firstValueFrom(this.authService.getUserData());
        
        // Verificar novamente após operação assíncrona
        if (!this.isComponentActive) {
          return;
        }
        
        if (!userData) {
          this.showMessage("error", await this.translate("user.login.errorLogin"));
          return;
        }

        if (userData.accessLevel < 1) {
          this.showMessage("error", await this.translate("user.login.accessDenied"));
          return;
        }

        // Tokens já gravados no sessionStorage pelo CognitoService — espelha p/ localStorage.
        this.sessionPersistence.persist();

        // Retorno de um 401 (ex.: link de projeto) tem prioridade sobre fromQr/feed.
        const redirect = this.authService.consumePostLoginRedirect();
        if (redirect) {
          this.router.navigateByUrl(redirect);
          return;
        }

        // Validar se veio da página do qrcode
        const fromQr = sessionStorage.getItem('fromQr');
        if (fromQr === 'true') {
          localStorage.removeItem('fromQr');
          sessionStorage.removeItem('fromQr');
          this.router.navigate(['/events']);
        } else {
          this.router.navigate([this.dashboardUrl]);
        }
        return;
      }
    }

    // Verificar novamente antes de navegações de challenge
    if (!this.isComponentActive) {
      return;
    }

    // Tratamento de challenges (MFA, nova senha, etc)
    if (result.challengeName === "PASSWORD_VERIFIER" || 
        result.challengeName === "CUSTOM_CHALLENGE") {
      sessionStorage.setItem("session", result.session || '');
      sessionStorage.setItem("email", this.f.email.value);
      this.router.navigate([this.mfaAccountUrl]);
      return;
    }

    if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
      this.layout?.scene?.reset();
      this.isNewPassRequired = true;
      this.session = result.session || '';
      return;
    }

    this.showMessage("error", await this.translate("user.login.errorLogin"));
  }

  /**
   * Processa erros de autenticação
   */
  private async handleAuthError(error: any): Promise<void> {
    if (error.name === CognitoException.USER_NOT_CONFIRMED ||
        error.message === "UserNotConfirmedException") {
      await this.handleUnconfirmedUser(this.f.email.value);
      return;
    }

    // Redis de sessões fora do ar: não é credencial errada, o serviço volta sozinho. Pode vir
    // mapeado (503 do /global/login) ou cru (503 do /user/active logo depois do login).
    if (isSessionStoreUnavailable(error)) {
      this.showMessage("error", await this.translate("user.login.serviceUnavailable"));
      return;
    }

    if (error.name === CognitoException.NOT_AUTHORIZED) {
      this.showMessage("error", await this.translate("user.login.errorLogin"));
      return;
    }

    this.showMessage("error", await this.translate("user.login.errorLogin"));
  }

  private async handleUnconfirmedUser(email: string): Promise<void> {
    if (!this.isComponentActive) return;

    try {
      await this.authService.resendConfirmationCode(email);

      if (!this.isComponentActive) return;

      sessionStorage.setItem("email-activate-account", email);
      this.router.navigate([this.activateAccountUrl]);
    } catch {
      if (this.isComponentActive) {
        this.showMessage("error", await this.translate("errorConection"));
      }
    }
  }



  async automaticSubmit() {
    if (!this.email || !this.password) {
      this.showMessage("error", await this.translate("user.login.errorLogin"));
      return;
    }

    this.submitBtnLoading = true;

    try {
      sessionStorage.setItem("email", this.email);
      const result = await this.authService.initiateAuth(this.email, this.password);

      // Reutiliza os mesmos métodos de handleAuthResult e handleAuthError
      await this.handleAuthResult(result);
    } catch (error) {
      // Adapta handleAuthError para usar this.email em vez de this.f.email.value
      await this.handleAuthErrorAutomatic(error);
    } finally {
      this.submitBtnLoading = false;
    }
  }

  /**
   * Processa erros de autenticação para automaticSubmit
   */
  private async handleAuthErrorAutomatic(error: any): Promise<void> {
    if (!this.isComponentActive) return;

    if (error.name === CognitoException.USER_NOT_CONFIRMED ||
        error.message === "UserNotConfirmedException") {
      await this.handleUnconfirmedUser(this.email ?? "");
      return;
    }

    if (!this.isComponentActive) return;

    if (isSessionStoreUnavailable(error)) {
      this.showMessage("error", await this.translate("user.login.serviceUnavailable"));
      return;
    }

    if (error.name === CognitoException.NOT_AUTHORIZED) {
      this.showMessage("error", await this.translate("user.login.errorLogin"));
      return;
    }

    this.showMessage("error", await this.translate("user.login.errorLogin"));
  }


  fixLanguage(event?: any) {
    this.messageService.clear();
    const selected: Language = event && event.value ? event.value : this.f.language.value;
    if (!selected || this.language.name === selected.name) return; // sem mudança
    this.language = selected;
    this.lastLanguage = this.language;
    this.changeLanguage(this.language.name);
    sessionStorage.setItem('language', this.language.name);
    this.userInteractedLanguage = true;
    // Atualiza estado disabled
    this.refreshLanguageOptions();
  }

  selectLanguage(selected: Language) {
    this.f.language.setValue(selected);
    this.fixLanguage({ value: selected });
  }

  refreshLanguageOptions() {
    for (let i = 0; i < this.languageOptions.length; i++) {
      if (this.languageOptions[i].name === this.f.language.value.name)
        this.languageOptions[i].disabled = true;
      else this.languageOptions[i].disabled = false;
    }
  }

  goDashboard() {
    this.router.navigate([this.dashboardUrl]);
  }

  setTitle() {
    this.translocoService
      .selectTranslate("title.login")
      .subscribe((value) => this.titleService.setTitle(value)).unsubscribe();
  }

  getLanguage(lan: string | null): Language {
    for (let i = 0; i < this.languageOptions.length; i++) {
      if (this.languageOptions[i].name === lan) {
        return this.languageOptions[i];
      }
    }
    return this.DEFAULT_LANGUAGE; // já é EN_US
  }

  private normalizeLanguage(lan: string | null): string {
    const allowed = [LanguageEnum.PT_BR, LanguageEnum.EN_US, LanguageEnum.ES_ES];
    if (!lan || !allowed.includes(lan as LanguageEnum)) {
      return LanguageEnum.EN_US;
    }
    return lan as string;
  }

  public changeLanguage(languageCode: string | null): void {
    if (languageCode !== null)
      this.translocoService.setActiveLang(languageCode);
  }

  getLocation() {
    this.geolocationService.getCountry().subscribe({
      next: (data: any) => {
        if (!this.userInteractedLanguage) {
          const geoLang = data.country_code === 'BR' ? this.languageOptions[0] : this.languageOptions[1];
          this.form.get('language')?.setValue(geoLang);
          this.language = geoLang;
          this.lastLanguage = geoLang;
          sessionStorage.setItem('language', geoLang.name);
          this.changeLanguage(geoLang.name);
          this.refreshLanguageOptions();
        }
      },
      error: (_) => {
        if (!this.userInteractedLanguage) {
          const fallback = this.languageOptions.find(l => l.name === LanguageEnum.EN_US) || this.languageOptions[1];
          this.form.get('language')?.setValue(fallback);
          this.language = fallback;
          this.lastLanguage = fallback;
          sessionStorage.setItem('language', fallback.name);
          this.changeLanguage(fallback.name);
          this.refreshLanguageOptions();
        }
      }
    });
  }
  get f() {
    return this.form.controls;
  }

  createChangePasswordForm() {
    this.changePasswordForm = this.formBuilderPass.group({
      email: [this.f.email.value],
      newPassword: ["", CustomValidator.getRequired()],
    });
  }

  challengeNewPass() {
    this.messagesPassword = [];
    this.submitBtnLoading = true;
    this.checkPasswordInputs();
    if(this.formPass.newPassword.valid) {
      this.authService.changeNewRequiredPass(this.session, this.f.email.value, this.formPass.newPassword.value).then(
        (response) => {
          // Verificar se componente ainda está ativo
          if (!this.isComponentActive) return;
          
          sessionStorage.setItem("email", this.f.email.value);
          // Tokens já armazenados pelo CognitoService via tap()
          if(response.authenticationResult) {
              if (this.accountDeletion.isPending) {
                this.sessionPersistence.persist();
                this.submitBtnLoading = false;
                this.router.navigate([PENDING_DELETION_ROUTE]);
                return;
              }
              this.accountService.findUserActive().subscribe((data) => {
                // Verificar se componente ainda está ativo
                if (!this.isComponentActive) return;
                
                if (data) {
                  sessionStorage.setItem("nameUser", data.name);
                  sessionStorage.setItem("language", data.language);
                  if (data.accessLevel < 1) {
                    this.translocoService
                      .selectTranslate("user.login.accessDenied")
                      .subscribe(
                        (value) =>
                          this.showMessage("error", value)
                      ).unsubscribe();
                  } else {
                    this.sessionPersistence.persist();
                    const redirect = this.authService.consumePostLoginRedirect();
                    if (redirect) { this.router.navigateByUrl(redirect); return; }
                    this.router.navigate([this.dashboardUrl]);
                  }
                } else {
                  this.translocoService
                    .selectTranslate("user.login.errorLogin")
                    .subscribe(
                      (value) =>
                        this.showMessage("error", value)
                    ).unsubscribe();
                }
                this.submitBtnLoading = false;
              });
          }
        }).catch(async (error) => {
          // Sem este catch o botão ficava preso em loading e o 503 não dava feedback algum.
          this.submitBtnLoading = false;
          if (this.isComponentActive && isSessionStoreUnavailable(error)) {
            this.showMessage("error", await this.translate("user.login.serviceUnavailable"));
          }
        });
    } else {
      this.submitBtnLoading = false;
      this.translocoService
      .selectTranslate("inputRequired")
        .subscribe(
          (value) =>
            this.showMessage("error", value)
        ).unsubscribe();
    }
  }

  get formPass() {
    return this.changePasswordForm.controls;
  }

  checkPasswordInputs() {
    this.messagesPassword = [];
    if (this.formPass.newPassword.invalid)
      this.formPass.newPassword.markAsDirty();
  }

  private showMessage(
  severity: 'success' | 'error' | 'info' | 'warn',
  summary: string
): void {
  // Qualquer mensagem significa que o usuário continua nesta tela: o carro volta para a pista.
  this.layout?.scene?.reset();
  this.messageService.clear();
  this.messageService.add({
    key: 'toastMessages',
    severity,
    summary,
  });
}

}
